// @architect-approved: DIRECTORY_HOST_PATTERN - Custom resolution required: renders
// a persistent SpeedDial FAB overlay sliced directly from the pre-built directory
// manifest (useDirectoryManifest), not the standard useDirectoryHost single-page
// resolution pattern.
// @architect-approved: HARDCODED_COLORS - Floor-based color fallback default for
// the SpeedDial FAB (same spirit as the /dirm_ exemption, filename doesn't match
// that path substring).
/**
 * DirmFloatPattern — FLOAT layout renderer (pattern_id = 2)
 *
 * Renders a persistent SpeedDial overlay (FAB button) from the pre-built manifest.
 * Buttons sorted by display_order with floor-based color fallback.
 * Used by floating elevator overlays that persist across page navigation.
 *
 * Props: seq (Sequence_Num) → slices manifest → renders SpeedDial
 *
 * Created: 2026-03-22
 */

import { SpeedDial, SpeedDialAction, SpeedDialIcon } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { handleManifestButtonClick } from '../../../utils/directoryNavigation';
import { DEFAULT_ICON, FLOOR_COLORS, ICON_MAP } from './icon-map';
import { useDirectoryByComponent, useDirectoryManifest } from './useDirectoryManifest';

interface DirmFloatPatternProps {
    seq: number;
}

const DirmFloatPattern: React.FC<DirmFloatPatternProps> = ({ seq }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    // If navigated with a target directory context, render THAT directory's buttons;
    // otherwise fall back to component-based resolution (picks isAppDefault).
    const target_dir_seq = (location.state as { dirSeq?: number } | null)?.dirSeq;
    const dirByManifest = useDirectoryManifest(target_dir_seq ?? -1);
    const dirByComponent = useDirectoryByComponent(seq);
    const dir = (target_dir_seq != null && dirByManifest) ? dirByManifest : dirByComponent;

    // Map manifest buttons → SpeedDial actions, sorted by display_order
    const actions = useMemo(() => {
        if (!dir) return [];
        return dir.buttons
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((btn) => {
                // Calculate component sequence from button data
                const componentSeq = btn.child_sequence_num;
                const isDirectory = btn.target_dir_sequence_num != null;
                
                return {
                    key: btn.assignmentId,
                    icon: ICON_MAP[btn.displayIcon || ''] || DEFAULT_ICON,
                    name: isDirectory
                        ? (btn.target_directory_name || btn.target_header_text || btn.displayLabel)
                        : btn.displayLabel,
                    componentSeq,
                    isDirectory,
                    color: btn.iconColor || FLOOR_COLORS[Number(btn.floorNumber) || 0] || '#607D8B',
                    target_dir_sequence_num: btn.target_dir_sequence_num ?? null,
                    target_directory_name: btn.target_directory_name,
                    target_header_text: btn.target_header_text,
                    prefix: btn.prefix,
                    child_sequence_num: btn.child_sequence_num,
                    directRoute: btn.directRoute, // ✅ Pass through pre-resolved route
                };
            });
    }, [dir]);

    if (!dir || actions.length === 0) return null;

    return (
        <SpeedDial
            ariaLabel={dir.headerText || 'Floor Navigation'}
            sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                '& .MuiSpeedDial-fab': {
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' },
                },
            }}
            icon={<SpeedDialIcon />}
            onClose={() => setOpen(false)}
            onOpen={() => setOpen(true)}
            open={open}
            direction="up"
            transitionDuration={{ enter: 300, exit: 500 }}
        >
            {actions.map((action) => {
                // Find original manifest button by key (assignmentId)
                const manifestBtn = dir.buttons.find(b => b.assignmentId === action.key);
                if (!manifestBtn) return null;
                
                return (
                <SpeedDialAction
                    key={action.key}
                    icon={action.icon}
                    tooltipTitle={action.isDirectory 
                        ? (action.target_directory_name || action.target_header_text || `${action.prefix}-${action.child_sequence_num}`)
                        : action.name}
                    tooltipOpen
                    onClick={() => {
                        handleManifestButtonClick(
                            manifestBtn,
                            navigate,
                            dir.directorySequenceNum,
                            location.pathname
                        );
                        setOpen(false);
                    }}
                    sx={{
                        '& .MuiSpeedDialAction-fab': {
                            bgcolor: action.color,
                            '&:hover': { bgcolor: action.color, opacity: 0.9 },
                        },
                    }}
                />
                );
            })}
        </SpeedDial>
    );
};

export default DirmFloatPattern;
